import type { AttendanceBoard, AttendanceBoardRow } from '@erp/shared';
import { CalendarDays, ChevronLeft, ChevronRight, ListChecks, LogIn, LogOut } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, Card, DataTable, type DataTableColumn, EmptyState, Input, Skeleton } from '../../../components/ui/index.js';
import {
  attendanceErrorCode,
  formatMinutes,
  getAttendanceDay,
  punchEmployee,
  shiftDate,
  type BranchFilter,
} from '../../../lib/attendance.js';

import { AttendanceStatusBadge } from './AttendanceStatusBadge.js';
import { SessionsDialog } from './SessionsDialog.js';

export interface BranchOption { id: string; name: string }

interface Props {
  canManage: boolean;
  branches: BranchOption[];
  /** Bumped by the page when something outside the board (the clock card) changed a day. */
  refreshKey: number;
}

const SUMMARY_KEYS = ['present', 'late', 'working', 'absent', 'notYet', 'leave', 'off', 'holiday', 'missingCheckout'] as const;
const SUMMARY_TONE: Partial<Record<(typeof SUMMARY_KEYS)[number], string>> = {
  present: 'text-success',
  late: 'text-warning',
  working: 'text-info',
  absent: 'text-danger',
  missingCheckout: 'text-danger',
};

/**
 * The day board: every tracked employee's day, judged against their schedule.
 * The date starts empty so the SERVER picks today — the shop's today, which on
 * a UTC host is not necessarily the server's or the browser's.
 */
export function AttendanceBoardView({ canManage, branches, refreshKey }: Props): JSX.Element {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation();
  const [date, setDate] = useState<string | undefined>(undefined);
  const [branch, setBranch] = useState<BranchFilter>(undefined);
  const [board, setBoard] = useState<AttendanceBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openRow, setOpenRow] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      setBoard(await getAttendanceDay(date, branch));
    } catch {
      toast.error(t('attendance.errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [date, branch, t]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  const shown = board?.date ?? date;
  const isToday = board ? board.date === board.today : false;

  const punch = async (row: AttendanceBoardRow): Promise<void> => {
    setBusyId(row.employee.id);
    try {
      const r = await punchEmployee(row.employee.id, row.day.open ? 'out' : 'in');
      const time = (r.action === 'in' ? r.record.checkIn : r.record.checkOut ?? r.record.checkIn).slice(11);
      toast.success(`${row.employee.displayName} — ${t(r.action === 'in' ? 'attendance.board.clockedIn' : 'attendance.board.clockedOut', { time })}`);
      await load();
    } catch (err) {
      const code = attendanceErrorCode(err);
      toast.error(t(`attendance.errors.${code ?? 'generic'}`, { defaultValue: t('attendance.errors.generic') }));
    } finally {
      setBusyId(null);
    }
  };

  const columns: DataTableColumn<AttendanceBoardRow>[] = [
    {
      id: 'employee',
      header: t('attendance.columns.employee'),
      sortValue: (r) => r.employee.displayName,
      searchValue: (r) => `${r.employee.code} ${r.employee.displayName}`,
      cell: (r) => (
        <button type="button" className="text-start" onClick={() => setOpenRow(r.employee.id)}>
          <span className="font-mono text-xs text-fg-muted">{r.employee.code}</span>{' '}
          <span className="font-medium hover:underline">{r.employee.displayName}</span>
        </button>
      ),
    },
    {
      id: 'schedule',
      header: t('attendance.columns.schedule'),
      sortValue: (r) => r.employee.scheduleName ?? '',
      cellClassName: 'text-sm text-fg-muted',
      cell: (r) => r.employee.scheduleName ?? '—',
    },
    {
      id: 'status',
      header: t('attendance.columns.status'),
      sortValue: (r) => t(`attendance.status.${r.day.status}`),
      searchValue: (r) => t(`attendance.status.${r.day.status}`),
      cell: (r) => <AttendanceStatusBadge day={r.day} />,
    },
    {
      id: 'in',
      header: t('attendance.columns.in'),
      sortValue: (r) => r.firstIn ?? '',
      cellClassName: 'font-mono text-sm tabular-nums',
      cell: (r) => r.firstIn ?? '—',
    },
    {
      id: 'out',
      header: t('attendance.columns.out'),
      sortValue: (r) => r.lastOut ?? '',
      cellClassName: 'font-mono text-sm tabular-nums',
      cell: (r) => r.lastOut ?? '—',
    },
    {
      id: 'worked',
      header: t('attendance.columns.worked'),
      align: 'end',
      sortValue: (r) => r.day.workedMinutes,
      cellClassName: 'font-mono tabular-nums',
      cell: (r) => (r.sessions.length > 0 ? formatMinutes(r.day.workedMinutes) : '—'),
    },
    {
      id: 'early',
      header: t('attendance.columns.early'),
      align: 'end',
      sortValue: (r) => r.day.earlyLeaveMinutes + r.day.missedMinutes,
      cellClassName: 'font-mono tabular-nums',
      cell: (r) => {
        const m = r.day.earlyLeaveMinutes + r.day.missedMinutes;
        return m > 0 ? <span className="text-warning">{formatMinutes(m)}</span> : '—';
      },
    },
    {
      id: 'overtime',
      header: t('attendance.columns.overtime'),
      align: 'end',
      sortValue: (r) => r.day.overtimeMinutes,
      cellClassName: 'font-mono tabular-nums',
      cell: (r) => (r.day.overtimeMinutes > 0 ? <span className="text-success">{formatMinutes(r.day.overtimeMinutes)}</span> : '—'),
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      headClassName: 'w-24',
      cell: (r) => (
        <div className="flex justify-end gap-1">
          {canManage && isToday && r.day.status !== 'not_employed' && (
            <Button
              variant="ghost"
              size="sm"
              loading={busyId === r.employee.id}
              onClick={() => void punch(r)}
              title={r.day.open ? t('attendance.board.clockOut') : t('attendance.board.clockIn')}
              aria-label={`${r.day.open ? t('attendance.board.clockOut') : t('attendance.board.clockIn')} ${r.employee.code}`}
            >
              {r.day.open
                ? <LogOut className="h-4 w-4 text-warning rtl:rotate-180" aria-hidden />
                : <LogIn className="h-4 w-4 text-success rtl:rotate-180" aria-hidden />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpenRow(r.employee.id)}
            title={t('attendance.board.sessions')}
            aria-label={`${t('attendance.board.sessions')} ${r.employee.code}`}
          >
            <ListChecks className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ),
    },
  ];

  const selected = board?.rows.find((r) => r.employee.id === openRow) ?? null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-2">
        <Button variant="secondary" size="sm" onClick={() => shown && setDate(shiftDate(shown, -1))} aria-label={t('attendance.board.prev')} title={t('attendance.board.prev')}>
          <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
        </Button>
        <div className="w-44">
          <Input
            type="date"
            value={shown ?? ''}
            onChange={(e) => setDate(e.target.value || undefined)}
            aria-label={t('attendance.board.date')}
          />
        </div>
        <Button variant="secondary" size="sm" onClick={() => shown && setDate(shiftDate(shown, 1))} aria-label={t('attendance.board.next')} title={t('attendance.board.next')}>
          <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setDate(board?.today)} disabled={isToday} className="gap-1.5">
          <CalendarDays className="h-4 w-4" aria-hidden />
          {t('attendance.board.today')}
        </Button>
        {branches.length > 1 && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-muted">{tc('filters.branch', { defaultValue: 'Branch' })}</span>
            <select
              value={branch ?? ''}
              onChange={(e) => setBranch(e.target.value || undefined)}
              className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand"
            >
              <option value="">{tc('filters.allBranches', { defaultValue: 'All branches' })}</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              <option value="none">{t('attendance.board.noBranch')}</option>
            </select>
          </label>
        )}
        {board && (
          <span className="ms-auto text-xs text-fg-muted">
            {t('attendance.board.shopTime', { time: board.now.slice(11) })} · {board.timezone}
          </span>
        )}
      </div>

      {board && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-9">
          {SUMMARY_KEYS.map((k) => (
            <div key={k} className="rounded-lg border border-border bg-bg-subtle px-3 py-2">
              <div className="truncate text-[0.7rem] text-fg-muted">{t(`attendance.summary.${k}`)}</div>
              <div className={`font-mono text-xl font-semibold tabular-nums ${board.summary[k] > 0 ? SUMMARY_TONE[k] ?? '' : 'text-fg-subtle'}`}>
                {board.summary[k]}
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && !board ? (
        <Card className="p-4"><Skeleton className="h-40 w-full" /></Card>
      ) : board && board.rows.length === 0 ? (
        <EmptyState
          icon={<CalendarDays className="h-8 w-8" aria-hidden />}
          title={t('attendance.board.empty')}
          description={t('attendance.board.emptyDescription')}
        />
      ) : board ? (
        <Card className={`overflow-hidden p-0 ${loading ? 'opacity-60' : ''}`}>
          <DataTable
            columns={columns}
            data={board.rows}
            rowKey={(r) => r.employee.id}
            pageSize={50}
            searchPlaceholder={t('attendance.board.search')}
          />
        </Card>
      ) : null}

      {selected && board && (
        <SessionsDialog
          open={openRow !== null}
          onOpenChange={(o) => { if (!o) setOpenRow(null); }}
          employee={selected.employee}
          date={board.date}
          sessions={selected.sessions}
          canManage={canManage}
          onChanged={load}
        />
      )}
    </div>
  );
}
